import { HttpParams } from '@angular/common/http'
import { Component, Input, OnDestroy, OnInit, WritableSignal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { DataMap } from '@grapecity/wijmo.grid'

import {
    AutoSizeDirective,
    BaseComponent,
    FeatureType,
    Grid,
    ListItem,
    ModalSize,
    QualityControlConcrete,
    VpJob,
} from '../../../shared'
import { ActionToolbarButtonsComponent } from '../../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { ConcreteModalComponent } from './concrete-modal.component'

@Component({
    selector: 'app-quality-control-concrete',
    templateUrl: './concrete.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjGridModule,
        WjGridFilterModule,
        WjInputModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        AutoSizeDirective,
    ],
})
export class ConcreteComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() selectedJob!: WritableSignal<string>
    @Input() allJobs: VpJob[] = []

    grid: Grid<QualityControlConcrete>
    concreteVendors: any[] = []
    supplierMap!: DataMap

    constructor() {
        super()
        this.grid = new Grid<QualityControlConcrete>(this.services)
            .setupModalEditing(
                this.services.data.qualityControlConcretes,
                'Concrete Quality Control',
                ConcreteModalComponent,
                () => new QualityControlConcrete(),
                this,
                ModalSize.large,
            )
            .filterBy(item => [item.structureType, item.partOfTheStructure])
            .withColumnFilters()
            .isReadOnly(true)
            .wrapColumnHeaders()

        this.grid.itemAdded.on(this.handleItemAdded.bind(this))
        this.grid.modified.on(this.refreshData.bind(this))
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPlansAndSpecsEdit])
    }

    get selectedJobName(): string {
        const jobId = this.selectedJob()
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId)
        return job ? `${job.id} ${job.name}` : jobId
    }

    ngOnInit() {
        this.loadVendors()
    }

    ngOnDestroy(): void {
        this.grid.itemAdded.off(this.handleItemAdded.bind(this))
        this.grid.modified.off(this.refreshData.bind(this))
        this.grid.destroy()
    }

    loadVendors() {
        this.services.data.get<any[]>('api/Vendor/GetForConcrete').subscribe({
            next: (result: any[]) => {
                this.concreteVendors = result
                this.buildSupplierMap()
            },
            error: () => {
                this.services.notification.error('Failed to load vendors')
            },
        })
    }

    buildSupplierMap() {
        const vendorOptions: ListItem<number | null>[] = []
        vendorOptions.push(new ListItem<number | null>(null, ''))
        this.concreteVendors.forEach(x => {
            vendorOptions.push(new ListItem<number | null>(x.id, x.name))
        })
        this.supplierMap = new DataMap(vendorOptions, 'value', 'display')
    }

    loadConcreteData(jobId: string) {
        if (!jobId || jobId === '') {
            this.grid.items = []
            return
        }

        const params = new HttpParams().set('jobId', jobId)
        this.services.data.get<QualityControlConcrete[]>('api/QualityControl/GetByJob', params).subscribe({
            next: (result: QualityControlConcrete[]) => {
                this.grid.items = result || []
            },
            error: () => {
                this.services.notification.error('Failed to load concrete data')
                this.grid.items = []
            },
        })
    }

    handleItemAdded(item: QualityControlConcrete) {
        item.job = this.selectedJob()
    }

    refreshData() {
        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.loadConcreteData(jobId)
        }
    }

    onGridInitialized() {
        // Grid initialized
    }
}
